<template>
  <div>
    <v-container>
      <h2 class="text-h5 mb-4">Shops &nbsp;&nbsp; (Total: {{ shops.total }})</h2>

      <!-- Quick View Dialog -->
      <v-dialog v-model="quick_view_dialog" width="1200">
        <v-card>
          <v-card-title class="text-h5 grey lighten-2">
            Shop Quick View
            <v-spacer></v-spacer>
            <v-btn icon @click="quick_view_dialog = false">
              <v-icon>{{ mdiClose }}</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="mt-4" v-if="selected_shop">
            <v-row>
              <v-col cols="6">
                <v-card outlined>
                  <v-card-title class="subtitle-1">
                    <v-icon small class="mr-2">{{ mdiShop }}</v-icon>
                    Shop Details
                  </v-card-title>
                  <v-card-text>
                    <p><strong>Specifier:</strong> {{ selected_shop.specifier }}</p>
                    <p>
                      <strong>Token:</strong>
                      <span class="text-truncate d-inline-block" style="max-width: 200px; vertical-align: bottom">
                        {{ selected_shop.token }}
                      </span>
                      <v-btn x-small text color="primary" @click="copyToClipboard(selected_shop.token)">
                        <v-icon small>{{ mdiContentCopy }}</v-icon>
                      </v-btn>
                    </p>
                    <p><strong>Status:</strong>
                      <v-chip small :color="selected_shop.app_status == '1' ? 'success' : 'error'" text-color="white">
                        {{ selected_shop.app_status == '1' ? 'Active' : 'Inactive' }}
                      </v-chip>
                    </p>
                    <p><strong>Last Synced:</strong> {{ getTimeDifference(selected_shop.last_feed_refresh) }}</p>
                  </v-card-text>
                </v-card>

                <v-card outlined class="mt-4">
                  <v-card-title class="subtitle-1">
                    <v-icon small class="mr-2">{{ mdiSync }}</v-icon>
                    Sync Status
                  </v-card-title>
                  <v-card-text v-if="selected_shop.shopify_sync_shop">
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>Products Sync</v-list-item-title>
                          <v-list-item-subtitle>
                            <v-chip
                              x-small
                              :color="selected_shop.shopify_sync_shop.is_product_processing ? 'warning' : 'success'"
                              text-color="white"
                            >
                              {{ selected_shop.shopify_sync_shop.is_product_processing ? 'Processing' : 'Completed' }}
                            </v-chip>
                            <span v-if="selected_shop.shopify_sync_shop.product_last_processed_at" class="ml-2 caption">
                              Last: {{ getTimeDifference(selected_shop.shopify_sync_shop.product_last_processed_at) }}
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>Collections Sync</v-list-item-title>
                          <v-list-item-subtitle>
                            <v-chip
                              x-small
                              :color="selected_shop.shopify_sync_shop.is_collection_processing ? 'warning' : 'success'"
                              text-color="white"
                            >
                              {{ selected_shop.shopify_sync_shop.is_collection_processing ? 'Processing' : 'Completed' }}
                            </v-chip>
                            <span v-if="selected_shop.shopify_sync_shop.collection_last_processed_at" class="ml-2 caption">
                              Last: {{ getTimeDifference(selected_shop.shopify_sync_shop.collection_last_processed_at) }}
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>Metafields Sync</v-list-item-title>
                          <v-list-item-subtitle>
                            <v-chip
                              x-small
                              :color="selected_shop.shopify_sync_shop.is_metafield_processing ? 'warning' : 'success'"
                              text-color="white"
                            >
                              {{ selected_shop.shopify_sync_shop.is_metafield_processing ? 'Processing' : 'Completed' }}
                            </v-chip>
                            <span v-if="selected_shop.shopify_sync_shop.metafield_last_processed_at" class="ml-2 caption">
                              Last: {{ getTimeDifference(selected_shop.shopify_sync_shop.metafield_last_processed_at) }}
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>

                    <div class="mt-4" v-if="selected_shop.shopify_sync_shop.total_products || selected_shop.shopify_sync_shop.total_collections">
                      <p class="subtitle-2 mb-2">Progress</p>
                      <div class="mb-2">
                        <div class="d-flex justify-space-between">
                          <span class="caption">Products</span>
                          <span class="caption">{{ selected_shop.shopify_sync_shop.processed_products || 0 }} / {{ selected_shop.shopify_sync_shop.total_products || 0 }}</span>
                        </div>
                        <v-progress-linear
                          :value="(selected_shop.shopify_sync_shop.processed_products / selected_shop.shopify_sync_shop.total_products) * 100"
                          color="primary"
                          height="4"
                        ></v-progress-linear>
                      </div>
                      <div>
                        <div class="d-flex justify-space-between">
                          <span class="caption">Collections</span>
                          <span class="caption">{{ selected_shop.shopify_sync_shop.processed_collections || 0 }} / {{ selected_shop.shopify_sync_shop.total_collections || 0 }}</span>
                        </div>
                        <v-progress-linear
                          :value="(selected_shop.shopify_sync_shop.processed_collections / selected_shop.shopify_sync_shop.total_collections) * 100"
                          color="primary"
                          height="4"
                        ></v-progress-linear>
                      </div>
                    </div>
                  </v-card-text>
                  <v-card-text v-else class="text-center caption">
                    No sync information available
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="6">
                <v-card outlined>
                  <v-card-title class="subtitle-1">
                    <v-icon small class="mr-2">{{ mdiAccount }}</v-icon>
                    User Details
                  </v-card-title>
                  <v-card-text v-if="selected_shop.user">
                    <p><strong>Name:</strong> {{ selected_shop.user.name }}</p>
                    <p><strong>Email:</strong> {{ selected_shop.user.email }}</p>
                    <p><strong>Username:</strong> {{ selected_shop.user.username }}</p>
                    <p><strong>Last Visit:</strong> {{ getTimeDifference(selected_shop.user.lastvisitDate) }}</p>
                  </v-card-text>
                  <v-card-text v-else-if="selected_shop.uninstalled_client">
                    <p><strong>Shop Name:</strong> {{ selected_shop.uninstalled_client.shop_name }}</p>
                    <p><strong>Email:</strong> {{ selected_shop.uninstalled_client.shop_email }}</p>
                    <p><strong>Total Products:</strong> {{ selected_shop.uninstalled_client.total_product_on_feed }}</p>
                    <p><strong>Total Orders:</strong> {{ selected_shop.uninstalled_client.total_orders }}</p>
                  </v-card-text>
                  <v-card-text v-else class="text-center caption">
                    No user information available
                  </v-card-text>
                </v-card>

                <v-card outlined class="mt-4">
                  <v-card-title class="subtitle-1">
                    <v-icon small class="mr-2">{{ mdiStar }}</v-icon>
                    Rating & Review
                  </v-card-title>
                  <v-card-text v-if="selected_shop.review">
                    <div class="d-flex align-center">
                      <span class="mr-2">App Rating:</span>
                      <v-rating
                        v-model="selected_shop.review.app_rating"
                        background-color="orange lighten-3"
                        color="orange"
                        dense
                        readonly
                        small
                      ></v-rating>
                    </div>
                    <p class="mt-2" v-if="selected_shop.review.internal_review">
                      <strong>Internal Review:</strong><br>
                      {{ selected_shop.review.internal_review }}
                    </p>
                  </v-card-text>
                  <v-card-text v-else class="text-center caption">
                    No rating information available
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-card class="mb-4" style="padding: 20px">
        <v-row>
          <v-col md="2">
            <v-text-field
              v-model="filters.search_keyword"
              label="Search Keyword"
              outlined
              @change="fetchShops()"
              dense
              placeholder="Search Keyword"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col md="2">
            <v-select
              style="margin-right: 10px"
              v-model="filters.shop_state"
              dense
              :items="['All', 'Active', 'Uninstalled']"
              @change="fetchShops()"
              label="Shop State"
              outlined
            >
            </v-select>
          </v-col>
        </v-row>
      </v-card>
      <v-data-table :headers="headers" :items="shops.data" class="elevation-1" disable-pagination hide-default-footer>
        <template v-slot:body="{ items }">
          <tbody v-if="!loading_data">
            <tr v-for="item in items" :key="item.id">
              <td>
                <i>#{{ item.id }}</i>
              </td>
              <td class="shop-info-cell">
                <div class="shop-info">
                  <div class="shop-info__title-row">
                    <span class="shop-info__domain text-truncate">{{ item.specifier }}</span>
                    <span
                      class="shop-info__badge"
                      :class="item.app_status == '1' ? 'shop-info__badge--active' : 'shop-info__badge--inactive'"
                    >
                      {{ item.app_status == '1' ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                  <div class="shop-info__email text-truncate">
                    {{ getShopEmail(item) }}
                  </div>
                </div>
              </td>
              <td>
                <div v-if="item.plan_name">
                  <v-tooltip top v-if="item.custom_plans && item.custom_plans.length > 0">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        class="ma-1"
                        color="success"
                        text-color="white"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon left x-small>{{ mdiCheckCircle }}</v-icon>
                        {{ item.plan_name }}
                      </v-chip>
                    </template>
                    <span>
                      <div v-for="plan in item.custom_plans" :key="plan.id">
                        {{ plan.name }} - {{ plan.price ? `${plan.currency_code || 'USD'} ${plan.price}` : 'Free' }}
                      </div>
                    </span>
                  </v-tooltip>
                  <v-chip
                    v-else
                    small
                    class="ma-1"
                    color="primary"
                    text-color="white"
                  >
                    {{ item.plan_name }}
                  </v-chip>
                </div>
                <v-chip
                  v-else
                  small
                  class="ma-1"
                  color="grey"
                  text-color="white"
                >
                  Unknown
                </v-chip>
              </td>
              <td>
                <span>
                  <v-chip
                    v-if="item.review"
                    small
                    class="ma-2"
                    :color="item.review.app_rating == 0 ? 'red lighten-4' : 'green lighten-4'"
                    :text-color="item.review.app_rating == 0 ? 'red' : 'green'"
                  >
                    {{ item.review.app_rating == 0 ? 'No' : 'Yes' }}
                  </v-chip>
                  <v-chip small class="ma-2" color="red" text-color="white" v-else>No</v-chip>
                </span>
              </td>

              <td>
                <v-chip
                  v-if="!item.etsy_shop"
                  small
                  class="ma-2"
                  color="black"
                >
                  No Etsy Shop
                </v-chip>
                <v-chip
                  v-else
                  small
                  class="ma-2"
                  :color="isEtsyTokenRevoked(item.etsy_shop) ? 'error' : 'success'"
                  text-color="white"
                >
                  {{ isEtsyTokenRevoked(item.etsy_shop) ? 'Revoked' : 'Not Revoked' }}
                </v-chip>
              </td>

              <td>
                {{ getTimeDifference(item.last_feed_refresh) }}
              </td>
              <td>
                <v-btn fab small color="info" class="table-action--button mr-2" @click="showQuickView(item)">
                  <v-icon small dark>{{ mdiEye }}</v-icon>
                </v-btn>
                <v-btn fab small color="primary darken-1" class="table-action--button" :loading="isShopLoading(item.id)" @click="loginAndOpenShopDetail(item.id)">
                <v-progress-circular
                  v-if="isShopLoading(item.id)"
                  indeterminate
                  color="white"
                  size="20"
                ></v-progress-circular>
                <v-icon v-else small dark>{{ mdiLogin }}</v-icon>
              </v-btn>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td colspan="7" class="text-center" style="padding: 80px 0px">
                <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
      <v-pagination
        v-model="shops.current_page"
        class="my-4"
        @input="handlePageChange"
        :length="shops.last_page"
        :total-visible="7"
      ></v-pagination>
    </v-container>
  </div>
</template>

<script>
import { mdiLogin, mdiLoading, mdiEye, mdiShop, mdiSync, mdiAccount, mdiStar, mdiContentCopy, mdiClose, mdiCheckCircle } from '@mdi/js'
import moment from "moment";
import { mapActions } from "vuex";

export default {
  data() {
    return {
      shopLoadingStates: {},
      loading_data: false,
      is_first: true,
      selectedRating: 0,
      filters: {
        search_keyword: '',
        shop_state: 'Active',
      },
      shop: {
        etsy_shop: {},
      },
      headers: [
        {
          text: 'ID',
          value: 'id',
        },
        {
          text: 'Shop',
          value: 'specifier',
          sortable: false,
        },
        { text: 'Plan Name', value: 'plan_name', sortable: false },
        { text: 'Rating', value: 'rating', sortable: false },
        { text: 'Token Revoked', value: 'is_token_revoked', sortable: false },
        { text: 'Last Synced At', value: 'last_feed_refresh' },
        { text: 'Actions', value: 'actions' },
      ],
      shops: {
        current_page: 1,
        data: [],
      },
      quick_view_dialog: false,
      selected_shop: null,

      //icons
      mdiLogin: mdiLogin,
      mdiLoading: mdiLoading,
      mdiEye: mdiEye,
      mdiShop: mdiShop,
      mdiSync: mdiSync,
      mdiAccount: mdiAccount,
      mdiStar: mdiStar,
      mdiContentCopy: mdiContentCopy,
      mdiClose: mdiClose,
      mdiCheckCircle: mdiCheckCircle
    }
  },
  mounted() {
    this.fetchShops()
  },
  methods: {
    ...mapActions(["LoginAsClient"]),
    handlePageChange(value) {
      this.fetchShops()
    },
    async fetchShops() {
      try {
        const currentPath = this.$route.path;
        let syncDelayedTimeinMinutes = '15';
        if (currentPath == '/shops') {
          syncDelayedTimeinMinutes ='';
        }

        // if (this.is_first == true)
        this.loading_data = true
        let response = await axios.get('admin/shops', {
          params: {
            page: this.shops.current_page,
            keyword: this.filters.search_keyword,
            state: this.filters.shop_state,
            min_last_synced_time: syncDelayedTimeinMinutes
          },
        })
        if (response.data.success) {
          this.shops = response.data.shops
          this.is_first = false
        }
      } catch (e) {
      } finally {
        this.loading_data = false
      }
    },

    async getShopDetail(shopId) {
      try {
        this.is_loading = true;
        let response = await axios.get(`admin/shops/${shopId}/get-detail`);
        if (response.data.success) {
          this.shop = response.data.shop;
        }
      } catch (e) {
      } finally {
        this.is_loading = false;
      }
    },

    async loginAndOpenShopDetail(shopId){
      this.setShopLoadingState(shopId, true);
      await this.getShopDetail(shopId);
      console.log(this.shop)
      await this.loginShop();
      window.location = "/shops/"+shopId;
    },

    async loginShop() {
      console.log(this.shop)
      let resposne = await this.LoginAsClient(this.shop);
      if (resposne.success != true) {
        this.$swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Email or password mismatch!",
        });
      } else {
        console.log("Logged in as Client Successfully!")
      }
    },

    getShopEmail(item) {
      if (item.user && item.user.email != null) {
        return item.user.email
      }

      if (item.uninstalled_client && item.uninstalled_client.shop_email != null) {
        return item.uninstalled_client.shop_email
      }

      return 'Not Available'
    },

    isEtsyTokenRevoked(etsyShop) {
      return parseInt(etsyShop.is_token_revoked) === 1;
    },

    getTimeDifference(lastRefreshTimestamp) {
      if (lastRefreshTimestamp == null || lastRefreshTimestamp == '1111-11-11 00:00:00') {
         return lastRefreshTimestamp + (lastRefreshTimestamp == '1111-11-11 00:00:00' ? ' (manual full sync)' : '');
      }
      const lastRefreshMoment = moment.utc(lastRefreshTimestamp);
      const nowUtcMoment = moment.utc();
      const timeDifferenceMinutes = nowUtcMoment.diff(lastRefreshMoment, 'minutes');
      const formattedTimeDifference = moment.duration(timeDifferenceMinutes, 'minutes').humanize();
      return formattedTimeDifference + ' ago';
    },

  setShopLoadingState(shopId, isLoading) {
    this.$set(this.shopLoadingStates, shopId, isLoading);
  },

  isShopLoading(shopId) {
    return this.shopLoadingStates[shopId] || false;
  },

  showQuickView(shop) {
    this.selected_shop = shop;
    this.quick_view_dialog = true;
  },

  copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      this.$swal.fire({
        icon: "success",
        title: "Copied!",
        text: "Token copied to clipboard.",
      });
    }).catch(() => {
      this.$swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to copy token.",
      });
    });
  },
}
}
</script>

<style scoped>
.shop-info-cell {
  min-width: 220px;
  max-width: 320px;
}

.shop-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.shop-info__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.shop-info__domain {
  flex: 0 1 auto;
  min-width: 0;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.87);
}

.shop-info__email {
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.45);
}

.shop-info__badge {
  flex-shrink: 0;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0.01em;
}

.shop-info__badge--active {
  background-color: #e8f3ec;
  color: #3d6b4f;
}

.shop-info__badge--inactive {
  background-color: #faeaea;
  color: #a85c5c;
}
</style>
